import { useEffect, useState, useRef, useCallback } from "react";
import { Col, Row } from "react-grid-system";
import {
  GetAccountResponse,
  GetPhoneOptionsResponse,
  PhoneContact,
} from "../../../lib/api/models";
import { useGetInactivationChangeMessages } from "../../../lib/api/queries";
import { formatInCentralTime } from "../../../lib/formatting";
import StyledButton from "../../common/buttons/StyledButton";
import TextButton from "../../common/buttons/TextButton";
import { UnsavedChangesModal } from "../../common/modals";
import AutomatedSystemsConsentModal from "../AutomatedSystemsConsentModal";
import Memo from "../Memo";
import MemoHistory from "../MemoHistory";
import ModifyPhoneActivationStatus from "../ModifyPhoneActivationStatus";
import ModifyPhoneConsentInfo from "../ModifyPhoneConsentInfo";
import ModifyPhonePrimaryInfo from "../ModifyPhonePrimaryInfo";
import ModifyPhonesRestriction from "../ModifyPhonesRestriction";
import PartialSuccessMessagesModal from "../PartialSuccessMessagesModal";
import { PhoneNumberOverride, usePhoneNumberForm } from "../PhoneNumber.hooks";
import PhoneNumberDisplay from "../PhoneNumberDisplay";
import UpdatePhoneNumberModal from "../UpdatePhoneNumberModal";
import VerifyPhone from "../VerifyPhone";
import toolStyles from "../../../sharedStyles/Tool.module.css";
import styles from "./EditPhoneNumber.module.css";

const strings = {
  editNumberTitle: "Edit Number",
  cancel: "Cancel",
  reset: "Reset",
  close: "Close",
  addMemo: "Add Memo",
  memos: "Memos",
  update: "Update",
  inactivationChangeErrorTitle: "Failed to get inactivation change messages",
  samePhoneNumberWarning:
    "The same phone number is currently populated in both Primary and Secondary. Add another phone number to Primary or Secondary.",
};

type Props = {
  account: GetAccountResponse;
  phoneContact: PhoneContact;
  options: GetPhoneOptionsResponse;
  primaryNumber: string;
  secondaryNumber: string;
  onClickCancel: () => void;
  onClickClose: () => void;
  onVerify: (phoneId: string) => void;
  isVerifyLoading: boolean;
  setToolInProgress: () => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  referenceId?: string;
};

export default function EditPhoneNumber({
  account,
  phoneContact,
  options,
  primaryNumber,
  secondaryNumber,
  onClickCancel,
  onClickClose,
  onVerify,
  isVerifyLoading,
  setToolInProgress,
  onDisplayGeneralErrors,
  referenceId,
}: Props) {
  const [isCloseWithoutSavingModelOpen, setIsCloseWithoutSavingModelOpen] =
    useState(false);
  const [isASCModalOpen, setASCModalOpen] = useState(false);
  const [isUpdatePhoneNumberModalOpen, setIsUpdatePhoneNumberModalOpen] =
    useState(false);

  const [
    formState,
    {
      generalDispatch,
      validationMessages,
      isLoading,
      checkOverridesAndSubmit,
      resetForm,
      partialSuccessMessages,
      onClosePartialSuccessMessages,
      addOverride,
      clearOverrides,
    },
  ] = usePhoneNumberForm(
    account.currentDwbuid,
    account.partyId,
    onClickCancel,
    onDisplayGeneralErrors,
    phoneContact,
    referenceId,
  );

  const onClickSubmit = () => {
    const overrideToHandle = checkOverridesAndSubmit(
      primaryNumber,
      secondaryNumber,
    );
    switch (overrideToHandle) {
      case PhoneNumberOverride.ConsentStatus:
        setASCModalOpen(true);
        return;
      case PhoneNumberOverride.UpdatePhoneNumber:
        setIsUpdatePhoneNumberModalOpen(true);
        return;
    }
  };

  useEffect(() => {
    if (formState.isDirty) {
      setToolInProgress();
    }
  }, [formState.isDirty, setToolInProgress]);

  const {
    mutateAsync: getInactivationChangeMessages,
    data: inactivationChangeMessages,
    reset: resetInactivationChangeMessages,
  } = useGetInactivationChangeMessages();

  const consentRevoke = inactivationChangeMessages?.setConsentFields === "No";

  // For consentRevokeRef, we want to always use the most up-to-date consent revoke value
  // But we don't want to re-run the request every time the consent revoke value changes
  const consentRevokeRef = useRef(consentRevoke);
  useEffect(() => {
    if (inactivationChangeMessages) {
      consentRevokeRef.current =
        inactivationChangeMessages.setConsentFields === "No";
    }
  }, [inactivationChangeMessages]);

  // For onGetInactivationChangeMessages, we want to always use the most up-to-date phone number
  // But we don't want to re-run the request every time the phone number changes
  const phoneNumberRef = useRef<string>(formState.phoneNumber);
  useEffect(() => {
    phoneNumberRef.current = formState.phoneNumber;
  }, [formState.phoneNumber]);

  const oldInactivationId = useRef<string>(
    formState.active ? "-1" : (formState.inactivationReasonId ?? "0"),
  );

  const isInitialChangeMessageLoad = useRef(true);
  const skipGetInactivationChangeMessages = useRef(false);
  const onGetInactivationChangeMessages = useCallback(async () => {
    try {
      // If this is the initial load for change messages for an edited phone number
      // and the initial data is active, we know that the results will be empty, so skip making the request
      if (isInitialChangeMessageLoad.current && formState.active) {
        isInitialChangeMessageLoad.current = false;
        return;
      }
      // Otherwise updated the initial load value
      isInitialChangeMessageLoad.current = false;

      if (skipGetInactivationChangeMessages.current) {
        skipGetInactivationChangeMessages.current = false;
        return;
      }

      const originalInactivationId = phoneContact.inactivation
        ? (phoneContact.inactivation.inactivationReasonId ?? "0")
        : "-1";
      const currentInactivationId = formState.active
        ? "-1"
        : (formState.inactivationReasonId ?? "0");

      const res = await getInactivationChangeMessages({
        dwbuid: account.currentDwbuid,
        originalInactivationId: originalInactivationId,
        oldInactivationId: oldInactivationId.current,
        newInactivationId: currentInactivationId,
        isRestricted: formState.restricted,
        phoneNumber: phoneNumberRef.current,
        originalConsentStatus: phoneContact.consentStatus,
        newConsentStatus:
          formState.consentStatus ?? "PEC_NOT_SPECIFIED_OR_UNKNOWN",
        isEdit: true,
        agentSelectedPEC:
          !!formState.consentStatus && !consentRevokeRef.current,
      });

      if (
        res.setConsentFields === "Clear" &&
        formState.consentStatus !== undefined
      ) {
        skipGetInactivationChangeMessages.current = true;
        generalDispatch({
          consentStatus: undefined,
        });
      } else if (
        res.setConsentFields === "No" &&
        formState.consentStatus !== "PEC_NO"
      ) {
        skipGetInactivationChangeMessages.current = true;
        generalDispatch({
          consentStatus: "PEC_NO",
        });
      }

      oldInactivationId.current = currentInactivationId;
    } catch (err) {
      onDisplayGeneralErrors(err);
    }
  }, [
    account.currentDwbuid,
    formState.active,
    formState.consentStatus,
    formState.inactivationReasonId,
    formState.restricted,
    generalDispatch,
    getInactivationChangeMessages,
    onDisplayGeneralErrors,
    phoneContact.consentStatus,
    phoneContact.inactivation,
  ]);

  useEffect(() => {
    onGetInactivationChangeMessages();
  }, [onGetInactivationChangeMessages]);

  return (
    <>
      <Row>
        <Col md={2}>
          <h2 className={toolStyles.title}>{strings.editNumberTitle}</h2>
          <TextButton
            className={styles.cancelButton}
            onClick={() =>
              formState.isDirty
                ? setIsCloseWithoutSavingModelOpen(true)
                : onClickCancel()
            }
          >
            {strings.cancel}
          </TextButton>
        </Col>
        <Col md={4}>
          <PhoneNumberDisplay phoneContact={phoneContact} />
        </Col>
        <Col offset={{ md: 4 }} md={1}>
          <StyledButton
            variant="secondary"
            className={styles.wFull}
            onClick={() => {
              resetForm();
              resetInactivationChangeMessages();
            }}
            disabled={!formState.isDirty || isLoading}
          >
            {strings.reset}
          </StyledButton>
        </Col>
        <Col md={1}>
          <StyledButton
            variant="secondary"
            className={styles.wFull}
            onClick={onClickClose}
          >
            {strings.close}
          </StyledButton>
        </Col>
      </Row>
      <Row className={styles.minHeight16}>
        <Col>
          <ul className={styles.inactivationChangeMessages}>
            {inactivationChangeMessages?.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
          {phoneContact.isPrimary && phoneContact.isSecondary ? (
            <div className={styles.samePhoneNumberWarning}>
              {strings.samePhoneNumberWarning}
            </div>
          ) : null}
        </Col>
      </Row>
      <Row className={styles.mb16}>
        <Col md={3}>
          <ModifyPhonePrimaryInfo
            account={account}
            options={options}
            formState={formState}
            generalDispatch={generalDispatch}
            validationMessages={validationMessages}
            disabled={isLoading}
          />
        </Col>
        <Col md={3}>
          <ModifyPhoneConsentInfo
            prevConsentStatus={phoneContact.consentStatus}
            prevConsentMethod={phoneContact.consentSourceType}
            prevConsentUpdateDate={phoneContact.consentCapturedDate}
            prevActive={phoneContact.inactivation === null}
            options={options}
            formState={formState}
            generalDispatch={generalDispatch}
            validationMessages={validationMessages}
            disabled={isLoading}
            consentRevoke={consentRevoke}
            updateAllConsentLabel={
              inactivationChangeMessages?.updateAllConsentLabel ?? false
            }
          />
        </Col>
        <Col md={6}>
          <Row className={styles.mb16}>
            <Col md={12}>
              <VerifyPhone
                lastVerified={
                  phoneContact.lastVerifiedDate
                    ? formatInCentralTime(
                        phoneContact.lastVerifiedDate,
                        "MM/dd/yyyy",
                      )
                    : "-"
                }
                lastCaptured={
                  phoneContact.lastCapturedDate
                    ? formatInCentralTime(
                        phoneContact.lastCapturedDate,
                        "MM/dd/yyyy",
                      )
                    : "-"
                }
                firstCaptured={
                  phoneContact.firstCapturedDate
                    ? formatInCentralTime(
                        phoneContact.firstCapturedDate,
                        "MM/dd/yyyy",
                      )
                    : "-"
                }
                onVerify={() => onVerify(phoneContact.phoneID)}
                isLoading={isVerifyLoading}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <ModifyPhonesRestriction
                options={options}
                formState={formState}
                generalDispatch={generalDispatch}
                validationMessages={validationMessages}
                disabled={isLoading}
              />
            </Col>
            <Col md={6}>
              <ModifyPhoneActivationStatus
                options={options}
                formState={formState}
                generalDispatch={generalDispatch}
                validationMessages={validationMessages}
                disabled={isLoading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className={styles.mb16}>
        <Col md={6}>
          <h3 className={styles.label}>{strings.addMemo}</h3>
          <Memo
            value={formState.memo}
            onChange={(v) => generalDispatch({ memo: v })}
            isLoading={isLoading}
            validationMessages={validationMessages?.memo}
          />
        </Col>
        <Col md={6}>
          <h3 className={styles.label}>{strings.memos}</h3>
          <MemoHistory memos={phoneContact.memos} />
        </Col>
      </Row>
      <Row>
        <Col offset={{ md: 10 }} md={2}>
          <StyledButton
            className={styles.wFull}
            onClick={onClickSubmit}
            isLoading={isLoading}
            disabled={!formState.isComplete || !formState.isDirty}
          >
            {strings.update}
          </StyledButton>
        </Col>
      </Row>
      <UnsavedChangesModal
        isOpen={isCloseWithoutSavingModelOpen}
        onRequestClose={() => setIsCloseWithoutSavingModelOpen(false)}
        onConfirm={onClickCancel}
      />
      <AutomatedSystemsConsentModal
        isOpen={isASCModalOpen}
        consentYes={!formState.active && formState.consentStatus === "PEC_YES"}
        onRequestClose={() => {
          setASCModalOpen(false);
          if (
            formState.active ||
            options.inactivationReasons.find(
              (o) => o.id === formState.inactivationReasonId,
            )?.consentRevoke === false
          ) {
            generalDispatch({
              consentStatus: undefined,
            });
          }
          clearOverrides();
        }}
        onAccept={() => {
          setASCModalOpen(false);
          addOverride(PhoneNumberOverride.ConsentStatus);
          onClickSubmit();
        }}
      />
      <PartialSuccessMessagesModal
        partialSuccessMessages={partialSuccessMessages}
        onRequestClose={onClosePartialSuccessMessages}
      />
      <UpdatePhoneNumberModal
        isOpen={isUpdatePhoneNumberModalOpen}
        phoneType={formState.updateFiservCode}
        prevNumber={
          formState.updateFiservCode === "UPDATE_FDR_HOME"
            ? primaryNumber
            : secondaryNumber
        }
        newNumber={formState.phoneNumber}
        onRequestClose={() => {
          setIsUpdatePhoneNumberModalOpen(false);
          clearOverrides();
        }}
        onSuccess={() => {
          setIsUpdatePhoneNumberModalOpen(false);
          addOverride(PhoneNumberOverride.UpdatePhoneNumber);
          onClickSubmit();
        }}
      />
    </>
  );
}
